import {
  forwardRef,
  useId,
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Slot } from 'radix-ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/componentes/ui/tooltip';
import { cn } from '@/lib/utils';

type Props = Omit<ComponentPropsWithoutRef<'span'>, 'children'> & {
  /** O que o elemento faz ou significa — nunca a repetição do rótulo (CONVENCOES-UI §8.1). */
  texto: ReactNode;
  /**
   * Quando o elemento está desabilitado, o **motivo**. Ele substitui o `texto` no tooltip e vai também para
   * um `sr-only`, porque leitor de tela não abre tooltip.
   */
  desabilitadoPor?: string | null;
  lado?: 'top' | 'right' | 'bottom' | 'left';
  /** Um elemento só, que aceita ref e repassa props (o `TooltipTrigger` usa `asChild`). */
  children: ReactElement;
};

/**
 * Entre o `TooltipTrigger` e o elemento: tira o `data-state` do tooltip ("closed", "delayed-open") antes de
 * chegar ao filho. Sem isso ele **sobrescreve** o `data-state` do próprio elemento — a aba ativa deixa de
 * ser `active`, o checkbox marcado deixa de ser `checked` — e todo estilo `data-[state=…]` some (achado no
 * retrofit de 09/10/2026).
 */
const SemEstadoDoTooltip = forwardRef<HTMLElement, { children: ReactElement }>(
  function SemEstadoDoTooltip(props, ref) {
    const resto: Record<string, unknown> = { ...props };
    delete resto['data-state'];
    return <Slot.Root ref={ref} {...resto} />;
  },
);

/**
 * Tooltip padrão da aplicação: **todo** elemento interativo ou informativo tem um (decisão da Ingrid,
 * 09/10/2026; CONVENCOES-UI §8.1). O guardião `src/testes/dicas.test.ts` cobra.
 *
 * Abre no hover e no foco de teclado (Radix). Botão `disabled` não recebe nem um nem outro, então, com
 * `desabilitadoPor`, o gatilho vira um `span` focável em volta do elemento — sem isso quem usa teclado nunca
 * leria o porquê.
 *
 * Encaminha ref e props ao elemento: assim uma `Dica` pode ser filha direta de `DialogTrigger asChild` sem
 * engolir o clique que abre o diálogo.
 */
export const Dica = forwardRef<HTMLElement, Props>(function Dica(
  { texto, desabilitadoPor, lado, className, children, ...resto },
  ref,
) {
  const idMotivo = useId();

  /*
   * Com a prop `desabilitadoPor` presente (mesmo `null`), o gatilho é **sempre** o `span`, habilitado ou não.
   * Se a estrutura mudasse ao habilitar, o React remontaria o botão: o foco do teclado se perderia e quem
   * guardou a referência (teste, `ref`) ficaria com um nó morto.
   */
  if (desabilitadoPor !== undefined) {
    const motivo = desabilitadoPor ?? null;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            ref={ref}
            // Só focável quando desabilitado: habilitado, quem recebe o foco é o próprio elemento (o
            // `focusin` dele sobe até aqui e abre a dica do mesmo jeito).
            tabIndex={motivo ? 0 : undefined}
            aria-describedby={motivo ? idMotivo : undefined}
            {...resto}
            className={cn(
              'inline-flex rounded-md focus-visible:outline-2 focus-visible:outline-ring',
              className,
            )}
          >
            {children}
            {motivo ? (
              <span id={idMotivo} className="sr-only">
                Indisponível: {motivo}
              </span>
            ) : null}
          </span>
        </TooltipTrigger>
        <TooltipContent side={lado} className="max-w-xs">
          {motivo ?? texto}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild className={className} {...resto}>
        <SemEstadoDoTooltip ref={ref}>{children}</SemEstadoDoTooltip>
      </TooltipTrigger>
      <TooltipContent side={lado} className="max-w-xs">
        {texto}
      </TooltipContent>
    </Tooltip>
  );
});
